/**
 * Algebra/coalgebra interaction diagrams: distributive laws,
 * homomorphism squares, bialgebras, and bisimulations.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useId } from "react";
import { GRAY } from "../../../design-system/tokens.js";
import {
  DIAGRAM_MONO,
  STROKE,
  SUBTLE,
  WARN_COLOR,
  ArrowDefs,
  ObjectNode,
  Arrow,
  FunctorBox,
} from "./primitives.js";

/**
 * Distributive law diagram: shows the natural transformation
 * `λ : F ∘ G ⇒ G ∘ F` either succeeding (`mode="exists"`) or refusing
 * to exist (`mode="missing"` — the categorical statement of CSK's
 * compose-then-observe seam).
 *
 *  EXISTS:                          MISSING:
 *  F(G(X)) ── λ ──▶ G(F(X))        F(G(X)) ─╳▶ G(F(X))
 *      "compose builds with             "no coherent way to
 *       observations coherently"         swap the order"
 */
export function DistributiveLawDiagram({
  mode = "exists",
  accent = STROKE,
  ariaLabel,
}: {
  readonly mode?: "exists" | "missing";
  readonly accent?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  const isExists = mode === "exists";
  const lineColor = isExists ? accent : WARN_COLOR;
  const label =
    ariaLabel ??
    (isExists
      ? "Distributive law λ : F ∘ G ⇒ G ∘ F exists — the algebra and coalgebra cohere"
      : "Distributive law λ : F ∘ G ⇒ G ∘ F is missing — compose-then-observe is structurally inexpressible");
  return (
    <svg
      viewBox="0 0 360 180"
      role="img"
      aria-label={label}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={lineColor} />
      <FunctorBox cx={66} cy={80} label="GX" functor="F" accent={accent} />
      <FunctorBox cx={294} cy={80} label="FX" functor="G" accent={accent} />
      <Arrow
        x1={102}
        y1={80}
        x2={264}
        y2={80}
        label="λ"
        subLabel={isExists ? "natural iso" : "no candidate exists"}
        markerId={markerId}
        color={lineColor}
        variant={isExists ? "solid" : "dashed"}
        forbidden={!isExists}
      />
      <text
        x={180}
        y={36}
        textAnchor="middle"
        fontSize="12"
        fontFamily={DIAGRAM_MONO}
        fontWeight={700}
        fill={lineColor}
      >
        {isExists ? "F ∘ G  ⇒  G ∘ F" : "F ∘ G  ⇏  G ∘ F"}
      </text>
      <text
        x={180}
        y={134}
        textAnchor="middle"
        fontSize="11"
        fontFamily={DIAGRAM_MONO}
        fill={GRAY[600]}
      >
        {isExists
          ? "build-then-observe = observe-then-build (up to λ)"
          : "compose-then-observe has no closed-term name"}
      </text>
      <text x={180} y={156} textAnchor="middle" fontSize="10" fill={lineColor} fontWeight={700}>
        {isExists ? "✓ bialgebra coheres" : "✗ structurally inexpressible"}
      </text>
    </svg>
  );
}

/**
 * Algebra homomorphism square:
 *
 *   F(A) — α → A
 *    |F(h)    | h
 *    v        v
 *   F(B) — β → B
 *
 * The point of the square is that h preserves the algebra structure:
 * h ∘ α = β ∘ F(h). This is precisely what an "algebra-respecting morphism"
 * means and why F-algebras are richer than a state machine's bare s₀.
 */
export function AlgebraHomomorphismSquare({
  accent = STROKE,
  accentAlt = SUBTLE,
  ariaLabel = "Algebra homomorphism square h ∘ α = β ∘ F(h)",
}: {
  readonly accent?: string;
  readonly accentAlt?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  return (
    <svg
      viewBox="0 0 280 200"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />
      <FunctorBox cx={56} cy={48} label="A" accent={accent} />
      <ObjectNode cx={216} cy={48} label="A" accent={accent} />
      <FunctorBox cx={56} cy={156} label="B" accent={accentAlt} />
      <ObjectNode cx={216} cy={156} label="B" accent={accentAlt} />
      <Arrow x1={84} y1={48} x2={200} y2={48} label="α" markerId={markerId} color={accent} />
      <Arrow x1={84} y1={156} x2={200} y2={156} label="β" markerId={markerId} color={accentAlt} />
      <Arrow x1={56} y1={62} x2={56} y2={142} label="F(h)" markerId={markerId} color={GRAY[700]} />
      <Arrow x1={216} y1={62} x2={216} y2={142} label="h" markerId={markerId} color={GRAY[700]} />
      <text
        x={140}
        y={106}
        textAnchor="middle"
        fontSize="11"
        fontFamily={DIAGRAM_MONO}
        fill={GRAY[600]}
      >
        h ∘ α = β ∘ F(h)
      </text>
    </svg>
  );
}

/**
 * Bialgebra coherence diagram:
 *
 *   F(A) — α → A — γ → G(A)
 *    │              │
 *    F(γ)           G(α)
 *    │              │
 *    v              v
 *   F(G(A)) ─ λ → G(F(A))
 *
 * λ is the distributive law that reconciles the algebra and coalgebra.
 * When λ does not exist, the bialgebra is incoherent — and that is what
 * CSK exploits to make certain compose-then-observe traces unconstructable.
 */
export function BialgebraDiagram({
  accent = STROKE,
  ariaLabel = "Bialgebra coherence square with distributive law λ : F ∘ G ⇒ G ∘ F",
}: {
  readonly accent?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  return (
    <svg
      viewBox="0 0 360 220"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />
      <FunctorBox cx={48} cy={48} label="A" functor="F" accent={accent} />
      <ObjectNode cx={180} cy={48} label="A" accent={accent} />
      <FunctorBox cx={312} cy={48} label="A" functor="G" accent={accent} />
      <FunctorBox cx={48} cy={172} label="GA" functor="F" accent={accent} />
      <FunctorBox cx={312} cy={172} label="FA" functor="G" accent={accent} />
      <Arrow x1={76} y1={48} x2={164} y2={48} label="α" markerId={markerId} color={accent} />
      <Arrow x1={196} y1={48} x2={284} y2={48} label="γ" markerId={markerId} color={accent} />
      <Arrow x1={48} y1={62} x2={48} y2={158} label="F(γ)" markerId={markerId} color={GRAY[700]} />
      <Arrow
        x1={312}
        y1={62}
        x2={312}
        y2={158}
        label="G(α)"
        markerId={markerId}
        color={GRAY[700]}
      />
      <Arrow x1={76} y1={172} x2={284} y2={172} label="λ" markerId={markerId} color={accent} />
    </svg>
  );
}

/**
 * Coalgebra homomorphism square — the dual of AlgebraHomomorphismSquare.
 *
 * A coalgebra morphism h : (S, γ) → (T, δ) satisfies the dual commuting
 * square F(h) ∘ γ = δ ∘ h.  Reading the square:
 *
 *   S ── γ ─▶ F(S)
 *   |            |
 *   h          F(h)
 *   v            v
 *   T ── δ ─▶ F(T)
 *
 * This is the structural statement that h preserves the observation
 * discipline — what you observe in T is exactly the F-image of what
 * you observe in S.  Unlike the algebra direction (where h ∘ α = β ∘ F(h)
 * goes "before" F), the coalgebra law says F acts *first*, then h.
 */
export function CoalgebraHomomorphismSquare({
  accent = STROKE,
  accentAlt = SUBTLE,
  ariaLabel = "Coalgebra homomorphism square F(h) ∘ γ = δ ∘ h",
}: {
  readonly accent?: string;
  readonly accentAlt?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  return (
    <svg
      viewBox="0 0 300 210"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />
      <ObjectNode cx={52} cy={48} label="S" accent={accent} />
      <FunctorBox cx={232} cy={48} label="S" accent={accent} />
      <ObjectNode cx={52} cy={162} label="T" accent={accentAlt} />
      <FunctorBox cx={232} cy={162} label="T" accent={accentAlt} />
      <Arrow x1={66} y1={48} x2={212} y2={48} label="γ" markerId={markerId} color={accent} />
      <Arrow x1={66} y1={162} x2={212} y2={162} label="δ" markerId={markerId} color={accentAlt} />
      <Arrow x1={52} y1={62} x2={52} y2={148} label="h" markerId={markerId} color={GRAY[700]} />
      <Arrow
        x1={232}
        y1={62}
        x2={232}
        y2={148}
        label="F(h)"
        markerId={markerId}
        color={GRAY[700]}
      />
      <text
        x={150}
        y={110}
        textAnchor="middle"
        fontSize="11"
        fontFamily={DIAGRAM_MONO}
        fill={GRAY[600]}
      >
        F(h) ∘ γ = δ ∘ h
      </text>
    </svg>
  );
}

/**
 * Bisimulation pair: two states s₁, s₂ stepping in lockstep through F.
 *
 *   s₁ — γ → F(s₁)        s₂ — γ → F(s₂)
 *    \                  /
 *     R ──── F(R) ─────
 *
 * The relation R is the bisimulation; F(R) lifts it through one observation.
 */
export function BisimulationDiagram({
  accent = STROKE,
  highlightRelation = true,
  ariaLabel = "Bisimulation: states s₁ ~ s₂ with related observations",
}: {
  readonly accent?: string;
  readonly highlightRelation?: boolean;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  return (
    <svg
      viewBox="0 0 320 200"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />
      <ObjectNode cx={56} cy={48} label="s" subscript="1" accent={accent} />
      <FunctorBox cx={216} cy={48} label="s₁" accent={accent} />
      <ObjectNode cx={56} cy={152} label="s" subscript="2" accent={accent} />
      <FunctorBox cx={216} cy={152} label="s₂" accent={accent} />
      <Arrow x1={70} y1={48} x2={196} y2={48} label="γ" markerId={markerId} color={accent} />
      <Arrow x1={70} y1={152} x2={196} y2={152} label="γ" markerId={markerId} color={accent} />
      <Arrow
        x1={56}
        y1={62}
        x2={56}
        y2={140}
        label="R"
        markerId={markerId}
        color={highlightRelation ? accent : GRAY[500]}
        variant="dashed"
      />
      <Arrow
        x1={216}
        y1={62}
        x2={216}
        y2={140}
        label="F(R)"
        markerId={markerId}
        color={highlightRelation ? accent : GRAY[500]}
        variant="dashed"
      />
      <text
        x={140}
        y={106}
        textAnchor="middle"
        fontSize="11"
        fontFamily={DIAGRAM_MONO}
        fill={GRAY[600]}
      >
        (s₁, s₂) ∈ R ⇒ (γ s₁, γ s₂) ∈ F(R)
      </text>
    </svg>
  );
}
